if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((_newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // If the picked combination doesn't exist, request the closest real one instead.
        const optionValues = shouldSwapProduct
          ? selectedOptionValues
          : this.resolveToExistingCombination(selectedOptionValues, target.dataset.optionValueId) ||
            selectedOptionValues;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, optionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, {
                requestedOptionValues: optionValues,
                clickedOptionValueId: target.dataset.optionValueId,
                targetId: target.id,
                attempt: 0,
              }),
        });
      }

      // Uses the variant list rendered in product-variant-picker.liquid ([data-variant-combinations]).
      // If the selected combination isn't a real variant, returns the option value IDs of the closest real one:
      // it always keeps the value the shopper just clicked, then keeps as many of their other choices as possible
      // (earlier options like Size count most), then prefers in-stock variants.
      // Returns null when nothing needs to change or the list isn't on the page.
      resolveToExistingCombination(selectedOptionValues, clickedOptionValueId) {
        const variantSelects = this.variantSelectors;
        const json = variantSelects?.querySelector('[data-variant-combinations]')?.textContent;
        if (!json || !clickedOptionValueId || !selectedOptionValues?.length) return null;

        let combinations;
        try {
          combinations = JSON.parse(json);
        } catch (e) {
          return null;
        }

        const groups = Array.from(variantSelects.querySelectorAll('.product-form__input'));
        if (groups.length !== selectedOptionValues.length) return null;

        const groupValues = groups.map((group) =>
          Array.from(group.querySelectorAll('input[data-option-value-id], option[data-option-value-id]')).map(
            (element) => ({ id: element.dataset.optionValueId, name: element.value })
          )
        );
        const selectedNames = selectedOptionValues.map((id, i) => groupValues[i].find((v) => v.id === id)?.name);
        if (selectedNames.some((name) => name === undefined)) return null;

        const isSelectedCombination = (combination) =>
          combination.options.every((name, i) => name === selectedNames[i]);
        if (combinations.some(isSelectedCombination)) return null;

        const clickedIndex = groupValues.findIndex((values) => values.some((v) => v.id === clickedOptionValueId));
        if (clickedIndex === -1) return null;

        const optionCount = selectedNames.length;
        let best = null;
        let bestScore = -1;
        combinations.forEach((combination) => {
          if (combination.options.length !== optionCount) return;
          if (combination.options[clickedIndex] !== selectedNames[clickedIndex]) return;

          let score = 0;
          combination.options.forEach((name, i) => {
            if (i !== clickedIndex && name === selectedNames[i]) score += 2 ** (optionCount - i);
          });
          score = score * 2 + (combination.available ? 1 : 0);

          if (score > bestScore) {
            best = combination;
            bestScore = score;
          }
        });
        if (!best) return null;

        const resolved = best.options.map((name, i) => groupValues[i].find((v) => v.name === name)?.id);
        return resolved.some((id) => id === undefined) ? null : resolved;
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      // Fallback for products without the [data-variant-combinations] list (250+ variants).
      // When Shopify returns no variant for the requested combination, use its data-option-available flags.
      // Those flags are "top-down": each option's values are judged against the options before it.
      // So keep the clicked option and everything before it, and fix the first later option whose
      // selected value isn't available. Called again after each fix until a variant comes back.
      // Returns the new list of option value IDs, or null if there is nothing safe to change.
      findClosestExistingOptionValues(html, { requestedOptionValues, clickedOptionValueId }) {
        if (!requestedOptionValues?.length || !clickedOptionValueId) return null;

        const groups = Array.from(html.querySelectorAll('variant-selects .product-form__input'));
        if (groups.length !== requestedOptionValues.length) return null;

        const getValues = (group) =>
          Array.from(group.querySelectorAll('input[data-option-value-id], option[data-option-value-id]'));
        const isSelected = (element) =>
          element.tagName === 'OPTION' ? element.hasAttribute('selected') : element.hasAttribute('checked');

        // Only trust the flags if the response reflects exactly what was requested.
        const responseSelection = groups.map((group) => getValues(group).find(isSelected)?.dataset.optionValueId);
        if (responseSelection.some((id, index) => id !== requestedOptionValues[index])) return null;

        const clickedIndex = groups.findIndex((group) =>
          getValues(group).some(({ dataset }) => dataset.optionValueId === clickedOptionValueId)
        );
        if (clickedIndex === -1) return null;

        for (let index = clickedIndex + 1; index < groups.length; index++) {
          const values = getValues(groups[index]);
          if (values.find(isSelected)?.dataset.optionAvailable === 'true') continue;

          const replacement = values.find(({ dataset }) => dataset.optionAvailable === 'true');
          if (!replacement) return null;
          return requestedOptionValues.map((id, i) => (i === index ? replacement.dataset.optionValueId : id));
        }
        return null;
      }

      handleUpdateProductInfo(productUrl, resolveContext = null) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          const maxAttempts = this.variantSelectors?.querySelectorAll('.product-form__input').length || 0;
          if (!variant && resolveContext && resolveContext.attempt < maxAttempts) {
            const closestOptionValues = this.findClosestExistingOptionValues(html, resolveContext);
            if (closestOptionValues) {
              // Each retry fixes one more option, so this stops after at most one try per option.
              const nextContext = {
                ...resolveContext,
                requestedOptionValues: closestOptionValues,
                attempt: resolveContext.attempt + 1,
              };
              this.renderProductInfo({
                requestUrl: this.buildRequestUrlWithParams(productUrl, closestOptionValues),
                targetId: resolveContext.targetId,
                callback: this.handleUpdateProductInfo(productUrl, nextContext),
              });
              return;
            }
          }

          this.pickupAvailability?.update(variant);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateOptionValues(html);

          try {
            this.updateMedia(html, variant?.featured_media?.id);
          } catch (e) {
            console.error('updateMedia error:', e);
          }

          const updateSourceFromDestination = (id, shouldHide = (_source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const sectionWrapper = this.closest('.shopify-section') || document;

        if (!variantFeaturedMediaId) {
          // No native featured media — check if server response has metafield images to swap in
          const mediaGallery = sectionWrapper.querySelector('media-gallery');
          const mediaGallerySource = sectionWrapper.querySelector('media-gallery ul');
          const mediaGalleryDestination = html.querySelector('media-gallery ul');
          if (mediaGallerySource && mediaGalleryDestination && mediaGallery) {
            mediaGallerySource.innerHTML = mediaGalleryDestination.innerHTML;

            // Re-init main gallery slider so SliderComponent re-indexes slides
            const mainSlider = sectionWrapper.querySelector('[id^="GalleryViewer"]');
            if (mainSlider?.resetPages) mainSlider.resetPages();

            // Re-bind thumbnail click handlers
            const thumbSource = sectionWrapper.querySelector('[id^="GalleryThumbnails"] ul');
            const thumbDestination = html.querySelector('[id^="GalleryThumbnails"] ul');
            if (thumbSource && thumbDestination) {
              thumbSource.innerHTML = thumbDestination.innerHTML;
              thumbSource.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
                mediaToSwitch.querySelector('button')?.addEventListener(
                  'click',
                  mediaGallery.setActiveMedia.bind(mediaGallery, mediaToSwitch.dataset.target)
                );
              });
              const thumbSlider = sectionWrapper.querySelector('[id^="GalleryThumbnails"]');
              if (thumbSlider?.resetPages) thumbSlider.resetPages();
            }

            // Activate first image and reset counter
            const firstMediaId = mediaGallerySource.querySelector('[data-media-id]')?.dataset?.mediaId;
            if (firstMediaId) mediaGallery.setActiveMedia(firstMediaId);
            const counterTotal = sectionWrapper.querySelector('.slider-counter--total');
            const newCounterTotal = html.querySelector('.slider-counter--total');
            if (counterTotal && newCounterTotal) counterTotal.textContent = newCounterTotal.textContent;
            const counterCurrent = sectionWrapper.querySelector('.slider-counter--current');
            if (counterCurrent) counterCurrent.textContent = '1';

            // Update modal content for zoom
            const modalContent = this.productModal?.querySelector('.product-media-modal__content');
            const newModalContent = html.querySelector('product-modal .product-media-modal__content');
            if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

            // Re-init hover zoom on new images
            if (typeof enableZoomOnHover === 'function') enableZoomOnHover(2);
          }
          return;
        }

        const mediaGallerySource = sectionWrapper.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        if (!mediaGallerySource || !mediaGalleryDestination) return;

        const refreshSourceData = () => {
          if (typeof enableZoomOnHover === 'function') enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        const mediaGalleryDestinationItems = Array.from(
          mediaGalleryDestination.querySelectorAll('li[data-media-id]')
        );
        const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
        let shouldRefresh = false;

        for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
          if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
            mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
            shouldRefresh = true;
          }
        }

        for (let i = 0; i < mediaGallerySourceItems.length; i++) {
          if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
            mediaGallerySourceItems[i].remove();
            shouldRefresh = true;
          }
        }

        if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

        mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
          const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

          if (sourceData && sourceData.index !== destinationIndex) {
            mediaGallerySource.insertBefore(
              sourceData.item,
              mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
            );

            [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          }
        });

        const mediaGallery = sectionWrapper.querySelector(`media-gallery`);

        // Update thumbnails
        const thumbSource = sectionWrapper.querySelector('[id^="GalleryThumbnails"] ul');
        const thumbDestination = html.querySelector('[id^="GalleryThumbnails"] ul');
        if (thumbSource && thumbDestination) {
          thumbSource.innerHTML = thumbDestination.innerHTML;

          if (mediaGallery) {
            thumbSource.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
              mediaToSwitch.querySelector('button')?.addEventListener(
                'click',
                mediaGallery.setActiveMedia.bind(mediaGallery, mediaToSwitch.dataset.target)
              );
            });
          }

          const thumbSlider = sectionWrapper.querySelector('[id^="GalleryThumbnails"]');
          if (thumbSlider?.resetPages) thumbSlider.resetPages();
        }

        // Re-init main gallery slider
        const mainSlider = sectionWrapper.querySelector('[id^="GalleryViewer"]');
        if (mainSlider?.resetPages) mainSlider.resetPages();

        // Set active media
        const activeMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;

        if (activeMediaId && mediaGallery) {
          mediaGallery.setActiveMedia(activeMediaId, true);
        }

        // Update slide counter
        const counterTotal = sectionWrapper.querySelector('.slider-counter--total');
        const newCounterTotal = html.querySelector('.slider-counter--total');
        if (counterTotal && newCounterTotal) counterTotal.textContent = newCounterTotal.textContent;
        const counterCurrent = sectionWrapper.querySelector('.slider-counter--current');
        if (counterCurrent) counterCurrent.textContent = '1';

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
